import { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Button, Alert, Row, Col } from 'react-bootstrap';
import { useLocaleContext } from '../../i18n';
import {
  FuelIntervalRequest,
  FuelStrategyResponse,
  ProductInfo,
  ProductType,
  StrategyOptions,
} from '../../models';
import { calculateStrategy } from '../../api/Api';
import PlannerNavigation from './PlannerNavigation';
import ProductRow, { ProductEntry, DIVISIBLE_TYPES, defaultMinPortion } from './ProductRow';
import { getDefaultProducts } from '../../data/defaultProducts';

interface Props {
  fuelIntervals: FuelIntervalRequest[];
  totalDuration: string;
  strategyOptions: StrategyOptions;
  onComplete: (strategy: FuelStrategyResponse) => void;
  onBack: () => void;
  onProductsChange: (products: ProductInfo[]) => void;
}

const STORAGE_KEY = 'broomwagon-settings';

function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export default function ProductsForm({ fuelIntervals, totalDuration, strategyOptions, onComplete, onBack, onProductsChange }: Props) {
  const { locale } = useLocaleContext();
  const nextId = useRef(0);
  const toEntry = (p: ProductInfo): ProductEntry => ({ ...p, _id: nextId.current++ });

  const s = loadSettings();
  const [products, setProducts] = useState<ProductEntry[]>(() => (s.products ?? getDefaultProducts()).map(toEntry));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newlyAddedId, setNewlyAddedId] = useState<number | null>(null);

  const strippedProducts = useCallback(() => products.map(({ _id, ...p }) => p), [products]);

  const sortedProducts = useCallback(() => {
    return [...products].sort((a, b) => {
      const aVerified = a.verified ?? false;
      const bVerified = b.verified ?? false;
      if (aVerified !== bVerified) {
        return aVerified ? -1 : 1;
      }
      if (aVerified) {
        const aRank = a.rank ?? 0;
        const bRank = b.rank ?? 0;
        return bRank - aRank;
      }
      return 0;
    });
  }, [products]);

  useEffect(() => {
    try {
      const existing = loadSettings();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...existing,
        products: strippedProducts(),
      }));
    } catch { /* quota exceeded */ }
  }, [products, strippedProducts]);

  const addProduct = useCallback(() => {
    const newId = nextId.current;
    setProducts(prev => [...prev, { name: '', carbs: 0, type: 'GEL', availableQuantity: 1, minDivisiblePortionGrams: undefined, _id: newId }]);
    nextId.current++;
    setNewlyAddedId(newId);
  }, []);

  const removeProduct = useCallback((id: number) => {
    setProducts(prev => prev.filter(p => p._id !== id));
  }, []);

  const updateProduct = useCallback((id: number, field: keyof ProductInfo, value: string | number | boolean) => {
    setProducts(prev => prev.map(p => {
      if (p._id !== id) return p;
      const updated = { ...p, [field]: value };
      if (field === 'type') {
        const newType = value as ProductType;
        if (DIVISIBLE_TYPES.has(newType) && !p.minDivisiblePortionGrams) {
          updated.minDivisiblePortionGrams = defaultMinPortion(newType, p.carbs);
        }
      }
      if (field === 'availableQuantity') {
        const newQty = value as number;
        if (newQty > 0 && !(p.active ?? true)) {
          updated.active = true;
        }
      }
      return updated;
    }));
    if (newlyAddedId === id) {
      setNewlyAddedId(null);
    }
  }, [newlyAddedId]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const plain = strippedProducts().filter(p => (p.availableQuantity ?? 1) > 0);
      const result = await calculateStrategy({
        fuelIntervals,
        totalDuration,
        maxAbsorptionRatePerHour: strategyOptions.maxAbsorptionRatePerHour,
        maxSurplusGramsPerInterval: strategyOptions.maxSurplusGramsPerInterval,
        maxDeficitGramsPerInterval: strategyOptions.maxDeficitGramsPerInterval,
        products: plain,
      });
      onProductsChange(plain);
      onComplete(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to calculate strategy');
    } finally {
      setLoading(false);
    }
  }, [fuelIntervals, totalDuration, strategyOptions, strippedProducts, onComplete, onProductsChange]);

  return (
    <div className="d-flex flex-column gap-3">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setProducts(getDefaultProducts().map(toEntry))}>{locale.planner.products.resetDefaults}</Button>
            <Button size="sm" variant="outline-success" onClick={addProduct}>{locale.planner.products.addProduct}</Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-column">
            <Row className="g-2 px-1 mb-1 d-none d-md-flex" style={{ paddingRight: '80px' }}>
              <Col md={3}><small className="text-secondary">{locale.planner.products.name}</small></Col>
              <Col md={2}><small className="text-secondary">{locale.planner.products.carbs}</small></Col>
              <Col md={1}><small className="text-secondary">{locale.planner.products.type}</small></Col>
              <Col md={2}><small className="text-secondary">{locale.planner.products.qty}</small></Col>
              <Col md={2}><small className="text-secondary">{locale.planner.products.minPortion}</small></Col>
            </Row>
            {sortedProducts().map(entry => (
              <ProductRow key={entry._id} entry={entry} onUpdate={updateProduct} onRemove={removeProduct} autoEdit={entry._id === newlyAddedId} />
            ))}
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      <PlannerNavigation
        onBack={onBack}
        onNext={handleSubmit}
        loading={loading}
      />
    </div>
  );
}
